import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Calendar as CalendarIcon, Bell, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export function EditPositionDialog({
  open,
  onOpenChange,
  editingPosition,
  form,
  setForm,
  schoolTOCs,
  subBookings,
  isUpdating,
  onUpdate,
  onCancel
}) {
  // Check if a sub is booked on specific dates
  const isSubBookedOnDates = (subId, dates) => {
    const bookings = (subBookings || {})[subId] || [];
    return dates.some(date => bookings.some(b => b.date === date));
  };
  
  const getSubBookingInfo = (subId, dates) => {
    const bookings = (subBookings || {})[subId] || [];
    const matches = bookings.filter(b => dates.includes(b.date));
    return matches.map(b => b.school_name).filter(Boolean).join(', ');
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-slate-600" />
            Edit Position
          </DialogTitle>
          <DialogDescription>
            Update the position details or cancel the position.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
          {/* Dates */}
          <div className="space-y-2">
            <Label>Date(s)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.dates.length > 0 
                    ? `${form.dates.length} date(s) selected`
                    : 'Select dates'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="multiple"
                  selected={form.dates.map(d => parseISO(d))}
                  onSelect={(dates) => {
                    setForm(prev => ({
                      ...prev,
                      dates: dates ? dates.map(d => format(d, 'yyyy-MM-dd')).sort() : []
                    }));
                  }}
                  disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  className="rounded-md border"
                  data-testid="edit-position-calendar"
                />
              </PopoverContent>
            </Popover>
            {form.dates.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {form.dates.map((date) => (
                  <Badge key={date} variant="secondary" className="text-xs">
                    {format(parseISO(date), 'MMM d')}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          {/* Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input 
                type="time" 
                value={form.start_time}
                onChange={(e) => setForm(prev => ({ ...prev, start_time: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <Input 
                type="time" 
                value={form.end_time}
                onChange={(e) => setForm(prev => ({ ...prev, end_time: e.target.value }))}
              />
            </div>
          </div>
          
          {/* Position Category */}
          <div className="space-y-2">
            <Label>Position Category</Label>
            <Input 
              value={form.grade_level}
              onChange={(e) => setForm(prev => ({ ...prev, grade_level: e.target.value }))}
              placeholder="e.g., Grade 3, High School Math"
            />
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label>Additional Notes</Label>
            <Textarea 
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Any additional information..."
              rows={3}
            />
          </div>
          
          {/* Sub Selection (only for open positions) */}
          {editingPosition && editingPosition.status === 'open' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Offered Subs</Label>
                {schoolTOCs.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => {
                      const activeTocs = schoolTOCs.filter(t => t.status !== 'blocked').map(t => t.user_id);
                      const allSelected = activeTocs.every(id => form.target_tocs.includes(id));
                      setForm(prev => ({
                        ...prev,
                        target_tocs: allSelected ? [] : activeTocs
                      }));
                    }}
                    data-testid="edit-select-all-tocs-btn"
                  >
                    {schoolTOCs.filter(t => t.status !== 'blocked').every(t => form.target_tocs.includes(t.user_id)) 
                      ? 'Deselect All' 
                      : 'Select All'}
                  </Button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2 bg-slate-50">
                {schoolTOCs.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-2">No Subs connected</p>
                ) : (
                  schoolTOCs.map((toc) => {
                    const isCurrentlyTargeted = form.target_tocs.includes(toc.user_id);
                    const hasResponded = editingPosition.responses?.[toc.user_id];
                    const responseStatus = hasResponded || null;
                    
                    // Check unavailability against selected dates
                    const selectedDateStrs = form.dates || [];
                    const todayStr = new Date().toISOString().split('T')[0];
                    const isUnavailableToday = toc.declined_all_today === todayStr && selectedDateStrs.includes(todayStr);
                    const unavailableDates = toc.declined_dates?.filter(d => selectedDateStrs.includes(d)) || [];
                    const isUnavailable = isUnavailableToday || unavailableDates.length > 0;
                    const allDatesUnavailable = isUnavailable && unavailableDates.length === selectedDateStrs.length;
                    
                    // Check if booked at another school
                    const isBooked = selectedDateStrs.length > 0 && isSubBookedOnDates(toc.user_id, selectedDateStrs);
                    const bookingInfo = isBooked ? getSubBookingInfo(toc.user_id, selectedDateStrs) : null;
                    
                    return (
                      <div 
                        key={toc.user_id} 
                        className={`flex items-center space-x-2 p-1.5 rounded text-sm ${
                          toc.status === 'blocked' ? 'bg-rose-50 opacity-60' : 
                          responseStatus === 'accept' ? 'bg-emerald-50' :
                          responseStatus === 'decline' ? 'bg-rose-50' :
                          isBooked ? 'bg-blue-50' :
                          allDatesUnavailable ? 'bg-amber-50' : 'bg-white'
                        }`}
                      >
                        <Checkbox
                          id={`edit-toc-${toc.user_id}`}
                          checked={isCurrentlyTargeted}
                          disabled={toc.status === 'blocked' || responseStatus === 'accept'}
                          onCheckedChange={(checked) => {
                            setForm(prev => ({
                              ...prev,
                              target_tocs: checked
                                ? [...prev.target_tocs, toc.user_id]
                                : prev.target_tocs.filter(id => id !== toc.user_id)
                            }));
                          }}
                        />
                        <label 
                          htmlFor={`edit-toc-${toc.user_id}`} 
                          className={`flex-1 cursor-pointer ${
                            isBooked ? 'text-blue-600' :
                            allDatesUnavailable ? 'line-through text-slate-400' : ''
                          }`}
                        >
                          {toc.last_name ? `${toc.last_name}, ${toc.first_name || toc.name}` : toc.name}
                        </label>
                        {responseStatus === 'accept' && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Accepted</Badge>
                        )}
                        {responseStatus === 'decline' && (
                          <Badge className="bg-rose-100 text-rose-700 text-xs">Declined</Badge>
                        )}
                        {isBooked && toc.status !== 'blocked' && !responseStatus && (
                          <span className="text-xs text-blue-600 font-medium" title={`Booked at: ${bookingInfo}`}>
                            Booked
                          </span>
                        )}
                        {isUnavailable && !isBooked && toc.status !== 'blocked' && !responseStatus && (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                            {allDatesUnavailable ? 'Unavailable' : `Unavailable ${unavailableDates.length}/${selectedDateStrs.length} days`}
                          </Badge>
                        )}
                        {isCurrentlyTargeted && !hasResponded && !isUnavailable && !isBooked && (
                          <Badge variant="outline" className="text-xs">Pending</Badge>
                        )}
                        {!isCurrentlyTargeted && !hasResponded && !isUnavailable && !isBooked && (
                          <Badge variant="outline" className="text-xs text-slate-400">Not offered</Badge>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
          
          {/* Reminder Settings (only for open positions) */}
          {editingPosition && editingPosition.status === 'open' && (
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Reminder Settings
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Frequency</Label>
                  <Select
                    value={String(form.reminder_frequency_minutes)}
                    onValueChange={(value) => setForm(prev => ({ 
                      ...prev, 
                      reminder_frequency_minutes: parseInt(value) 
                    }))}
                  >
                    <SelectTrigger data-testid="edit-reminder-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Every 1 min</SelectItem>
                      <SelectItem value="2">Every 2 min</SelectItem>
                      <SelectItem value="5">Every 5 min</SelectItem>
                      <SelectItem value="10">Every 10 min</SelectItem>
                      <SelectItem value="15">Every 15 min</SelectItem>
                      <SelectItem value="30">Every 30 min</SelectItem>
                      <SelectItem value="60">Every 1 hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Stop After</Label>
                  <Select
                    value={form.max_reminders === null ? 'unlimited' : String(form.max_reminders)}
                    onValueChange={(value) => setForm(prev => ({ 
                      ...prev, 
                      max_reminders: value === 'unlimited' ? null : parseInt(value) 
                    }))}
                  >
                    <SelectTrigger data-testid="edit-max-reminders">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unlimited">Unlimited</SelectItem>
                      <SelectItem value="3">3 reminders</SelectItem>
                      <SelectItem value="5">5 reminders</SelectItem>
                      <SelectItem value="10">10 reminders</SelectItem>
                      <SelectItem value="15">15 reminders</SelectItem>
                      <SelectItem value="20">20 reminders</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Saving restarts reminder counts for all pending Subs.
              </p>
            </div>
          )}
          
          {/* Position Info */}
          {editingPosition && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="text-slate-600">
                <strong>Status:</strong> {editingPosition.status.charAt(0).toUpperCase() + editingPosition.status.slice(1)}
              </p>
              {editingPosition.accepted_by_name && (
                <p className="text-emerald-600 flex items-center gap-1 mt-1">
                  <CheckCircle className="h-4 w-4" />
                  Filled by {editingPosition.accepted_by_name}
                </p>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row gap-2 flex-shrink-0 border-t pt-4">
          <Button
            variant="destructive"
            onClick={onCancel}
            disabled={isUpdating}
            className="w-full sm:w-auto"
            data-testid="cancel-position-btn"
          >
            <AlertTriangle className="h-4 w-4 mr-1" />
            Cancel Position
          </Button>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              Close
            </Button>
            <Button
              onClick={onUpdate}
              disabled={isUpdating || form.dates.length === 0}
              className="flex-1 sm:flex-none bg-slate-800 hover:bg-slate-700"
              data-testid="update-position-btn"
            >
              {isUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : null}
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
